import { ProductPriceContainerCtnrROV1, ScaledPriceROV1 } from "@ub/pricing-client"
import { getTranslations } from "next-intl/server"

import { formatPrice } from "@/lib/helper"
import { ReducedProductData } from "@/lib/storefront/product/product"

const FR_TAX_FACTOR = 1.2

export interface ScalePrice {
  pricePerUnit: number
  minQuantity: number
  currency?: string
}

export interface CardPrices {
  productId?: string
  listPrice: number
  salePrice: number
  discountPercent: number
  listPriceFormatted: string
  salePriceFormatted: string
  discountPercentFormatted: string
  isSinglePrice?: boolean
  scalePrices?: ScalePrice[]
  quantity?: number
  currency: string
  singleNetSalePrice?: number
}

export interface DiscountDisplayOptions {
  candidates?: Array<number | null | undefined>
  thresholdPercentagePoints?: number
}

const DEFAULT_DISCOUNT_DISPLAY_THRESHOLD_PERCENTAGE_POINTS = 1
const SOURCE_DISCOUNT_PRECISION_DIGITS = 6

export function determineDisplayDiscountPercent(
  calculatedDiscountPercent: number,
  options?: DiscountDisplayOptions
): number {
  const validCandidates = (options?.candidates ?? []).filter(
    (candidate): candidate is number => typeof candidate === "number" && Number.isFinite(candidate) && candidate > 0
  )
  const threshold = options?.thresholdPercentagePoints ?? DEFAULT_DISCOUNT_DISPLAY_THRESHOLD_PERCENTAGE_POINTS
  const closestCandidate = validCandidates.reduce<number | undefined>((closest, candidate) => {
    if (closest === undefined) {
      return candidate
    }

    return Math.abs(candidate - calculatedDiscountPercent) < Math.abs(closest - calculatedDiscountPercent)
      ? candidate
      : closest
  }, undefined)

  if (closestCandidate !== undefined && Math.abs(closestCandidate - calculatedDiscountPercent) <= threshold) {
    return Math.round(closestCandidate * 10 ** SOURCE_DISCOUNT_PRECISION_DIGITS) / 10 ** SOURCE_DISCOUNT_PRECISION_DIGITS
  }

  return Math.round(calculatedDiscountPercent)
}

export function createDiscountDisplayOptions(
  product: ReducedProductData | undefined,
  generalDiscount?: number,
  customerCategoryDiscount?: number
): DiscountDisplayOptions {
  return {
    candidates: [generalDiscount, product?.categoryDiscount?.rate, customerCategoryDiscount],
  }
}

export function formatDiscountPercent(discountPercent: number, isoLocale: string = "de-DE"): string {
  return (
    new Intl.NumberFormat(isoLocale, {
      minimumFractionDigits: 0,
      maximumFractionDigits: SOURCE_DISCOUNT_PRECISION_DIGITS,
    }).format(discountPercent) + "%"
  )
}

export function formatPackagingUnit(t: any, unit: string): string {
  if (unit) {
    return t("packaging_unit." + unit) === "packaging_unit." + unit
      ? t("packaging_unit.PCE")
      : t("packaging_unit." + unit)
  } else {
    return t("packaging_unit.PCE")
  }
}

export function generatePackagingInfoFromTranslations(
  t: any,
  article: ReducedProductData | undefined,
  doNotTranslateUnits?: boolean
) {
  if (
    article &&
    article.baseUnit &&
    article.baseUnit !== "" &&
    article.packingUnit &&
    article.packingUnit !== "" &&
    article.packingUnit !== article.baseUnit &&
    article.ratioBasePackingUnit > 1
  ) {
    return t("product.price_disclaimer", {
      packingUnit: doNotTranslateUnits ? article.packingUnit : formatPackagingUnit(t, article.packingUnit),
      ratioBasePackingUnit: article.ratioBasePackingUnit,
      baseUnit: doNotTranslateUnits ? article.baseUnit : formatPackagingUnit(t, article.baseUnit),
    })
  }

  return ""
}

export function generatePackagingInfoCompactFromTranslations(t: any, article: ReducedProductData | undefined) {
  if (
    article &&
    article.baseUnit &&
    article.baseUnit !== "" &&
    article.packingUnit &&
    article.packingUnit !== "" &&
    article.packingUnit !== article.baseUnit &&
    article.ratioBasePackingUnit > 1
  ) {
    return t("product.price_disclaimer_compact", {
      packingUnit: formatPackagingUnit(t, article.packingUnit),
      ratioBasePackingUnit: article.ratioBasePackingUnit,
      baseUnit: formatPackagingUnit(t, article.baseUnit),
    })
  }

  return ""
}

export function generateMinOrderInfoFromTranslations(t: any, article: ReducedProductData | undefined) {
  if (
    article &&
    article.minOrderQuantity &&
    article.minOrderQuantity > 0 &&
    article.packingUnit &&
    article.packingUnit !== ""
  ) {
    return t("product.min_order_quantity", {
      qty: article.minOrderQuantity,
      qty_unit: formatPackagingUnit(t, article.packingUnit),
    })
  }

  return ""
}

export function generateStepOrderInfoFromTranslations(t: any, article: ReducedProductData | undefined) {
  if (article && article.stepOrderQuantity && article.stepOrderQuantity > 1) {
    return t("product.step_order_quantity", {
      qty: article.stepOrderQuantity,
    })
  }

  return ""
}

async function getStorefrontTranslations(nextIntlLocale?: string) {
  return nextIntlLocale
    ? getTranslations({ locale: nextIntlLocale, namespace: "storefront" })
    : getTranslations("storefront")
}

export async function generatePackagingInfo(
  article: ReducedProductData | undefined,
  doNotTranslateUnits?: boolean,
  nextIntlLocale?: string
) {
  const t = await getStorefrontTranslations(nextIntlLocale)
  return generatePackagingInfoFromTranslations(t, article, doNotTranslateUnits)
}

export async function generatePackagingInfoCompact(article: ReducedProductData | undefined, nextIntlLocale?: string) {
  const t = await getStorefrontTranslations(nextIntlLocale)
  return generatePackagingInfoCompactFromTranslations(t, article)
}

export function generateSkuParams(productIds: string[]) {
  //["123456","654321","4634574"] -> "123456&sku=654321&sku=4634574

  if (productIds.length > 0) {
    return productIds.join("&sku=")
  }

  return ""
}

export function generateBasePriceInfo(
  isoLocale: string,
  article: ReducedProductData | undefined,
  salePrice: number | undefined
) {
  if (
    article &&
    salePrice &&
    article.basicPriceQuantity &&
    article.basicPriceUnit &&
    article.basicPriceUnit !== "" &&
    article.totalQuantity &&
    article.currency &&
    article.currency !== "" &&
    article.currency !== "N/A"
  ) {
    //console.log(article.currency)
    const pricePerUnit = salePrice / article.totalQuantity
    return `${formatPrice(isoLocale, pricePerUnit, article.currency)} / ${article.basicPriceQuantity} ${article.basicPriceUnit}`
  }

  return ""
}

export async function generateMinOrderInfo(article: ReducedProductData | undefined, nextIntlLocale?: string) {
  const t = await getStorefrontTranslations(nextIntlLocale)
  return generateMinOrderInfoFromTranslations(t, article)
}

export async function generateStepOrderInfo(article: ReducedProductData | undefined, nextIntlLocale?: string) {
  const t = await getStorefrontTranslations(nextIntlLocale)
  return generateStepOrderInfoFromTranslations(t, article)
}

export function determineCardPrices(
  isoLocale: string,
  isB2BCustomer: boolean,
  prices: ProductPriceContainerCtnrROV1 | undefined,
  ecoTax?: number,
  quantity?: number,
  originalProductId?: string,
  discountDisplayOptions?: DiscountDisplayOptions
): CardPrices {
  const effectiveQuantity = quantity !== undefined ? quantity : 1
  const productId = originalProductId ? originalProductId : prices?.data?.[0]?.sku
  if (productId) {
    if (prices?.data?.[0]?.prices?.MinListPrice?.[0]) {
      //if a MinListPrice exists, we assume this is a price object for a master product
      return determineCardPricesForMasterProduct(
        isoLocale,
        productId,
        isB2BCustomer,
        prices,
        ecoTax,
        effectiveQuantity,
        discountDisplayOptions
      )
    } else {
      return determineCardPricesForSingleArticle(
        isoLocale,
        productId,
        isB2BCustomer,
        prices,
        ecoTax,
        effectiveQuantity,
        discountDisplayOptions
      )
    }
  } else {
    return {
      productId: "",
      listPrice: 0,
      salePrice: 0,
      discountPercent: 0,
      listPriceFormatted: "-",
      salePriceFormatted: "-",
      discountPercentFormatted: "-",
      isSinglePrice: false,
      scalePrices: [],
      quantity: effectiveQuantity,
      currency: "EUR",
      singleNetSalePrice: 0,
    }
  }
}

//finds the index of the effective scale price row based on user selected quantity
function findEffectiveScalePriceIndex(priceData: any, quantity: number): number {
  if (quantity === 1) {
    return 0
  } else {
    if (priceData?.prices?.SalePrice && priceData?.prices?.SalePrice.length > 0) {
      let index = -1
      for (const scaledPrice of priceData?.prices?.SalePrice) {
        if (scaledPrice.minQuantity?.value) {
          const minQuantity = scaledPrice.minQuantity?.value
          if (quantity >= minQuantity) {
            index++
          }
        }
      }
      return index
    } else {
      return 0
    }
  }
}

//Uses the first ListPrice and SalePrice to determine prices for a single article
export function extractCardPriceForSingleArticlePriceData(
  isoLocale: string,
  productId: string,
  isB2BCustomer: boolean,
  priceData: any | undefined,
  ecoTax?: number,
  quantity?: number,
  discountDisplayOptions?: DiscountDisplayOptions
) {
  const effectiveQuantity = quantity !== undefined ? quantity : 1
  let scalePriceIndex = findEffectiveScalePriceIndex(priceData, effectiveQuantity)
  //console.dir(priceData,{depth:9})
  //ecoTax needs to be appended in FR
  const ecoTaxNet = ecoTax ? ecoTax : 0
  const ecoTaxGross = ecoTax ? ecoTax * FR_TAX_FACTOR : 0

  const currency: string = priceData?.prices?.ListPrice?.[0]?.net?.currency ?? "EUR"

  const listPrice: number = isB2BCustomer
    ? (priceData?.prices?.ListPrice?.[scalePriceIndex]?.net?.value ?? priceData?.prices?.ListPrice?.[0]?.net?.value) +
      ecoTaxNet
    : (priceData?.prices?.ListPrice?.[scalePriceIndex]?.gross?.value ??
        priceData?.prices?.ListPrice?.[0]?.gross?.value) + ecoTaxGross

  const salePrice: number = isB2BCustomer
    ? priceData?.prices?.SalePrice?.[scalePriceIndex]?.net?.value + ecoTaxNet
    : priceData?.prices?.SalePrice?.[scalePriceIndex]?.gross?.value + ecoTaxGross

  const listPriceFormatted = isB2BCustomer
    ? formatPrice(isoLocale, listPrice, currency)
    : addB2CStarIfNeeded(listPrice, formatPrice(isoLocale, listPrice, currency))

  const salePriceFormatted = isB2BCustomer
    ? formatPrice(isoLocale, salePrice, currency)
    : addB2CStarIfNeeded(salePrice, formatPrice(isoLocale, salePrice, currency))

  const discountPercent: number = salePrice > 0 && listPrice > 0 ? 100 - (salePrice / listPrice) * 100 : 0

  const discountPercentFormatted = formatDiscountPercent(
    determineDisplayDiscountPercent(discountPercent, discountDisplayOptions),
    isoLocale
  )

  const scalePrices: ScalePrice[] = []

  const singleNetSalePrice = priceData?.prices?.SalePrice?.[scalePriceIndex]?.net?.value

  if (priceData?.prices?.SalePrice?.length > 1) {
    for (const scaledPrice of priceData?.prices?.SalePrice) {
      const newScaledPrice = determineScaledPrice(isB2BCustomer, scaledPrice)
      scalePrices.push(newScaledPrice)
    }
  }

  return {
    listPrice: listPrice,
    salePrice: salePrice,
    discountPercent: discountPercent,
    listPriceFormatted: listPriceFormatted,
    salePriceFormatted: salePriceFormatted,
    discountPercentFormatted: discountPercentFormatted,
    isSinglePrice: !productId.toUpperCase().startsWith("E"),
    scalePrices: scalePrices,
    quantity: effectiveQuantity,
    currency: currency,
    singleNetSalePrice: singleNetSalePrice,
  }
}

export function determineCardPricesForSingleArticle(
  isoLocale: string,
  productId: string,
  isB2BCustomer: boolean,
  prices,
  ecoTax: number | undefined,
  quantity?: number,
  discountDisplayOptions?: DiscountDisplayOptions
) {
  const priceData = prices?.data?.[0]
  return extractCardPriceForSingleArticlePriceData(
    isoLocale,
    productId,
    isB2BCustomer,
    priceData,
    ecoTax,
    quantity,
    discountDisplayOptions
  )
}

function determineScaledPrice(isB2BCustomer: boolean, scaledPrice: ScaledPriceROV1): ScalePrice {
  const pricePerUnit = isB2BCustomer ? scaledPrice.net?.value : scaledPrice.gross?.value
  const currency = isB2BCustomer ? scaledPrice.net?.currency : scaledPrice.gross?.currency
  const minQuantity = scaledPrice.minQuantity?.value
  return {
    pricePerUnit: pricePerUnit ?? 0,
    currency: currency,
    minQuantity: minQuantity ?? 0,
  }
}

//Uses MinListPrice and MinSalePrice to determine prices for master products
function extractCardPriceFromMasterPriceData(
  isoLocale: string,
  productId: string,
  isB2BCustomer: boolean,
  priceData,
  ecoTax: number | undefined,
  quantity?: number,
  discountDisplayOptions?: DiscountDisplayOptions
) {
  const effectiveQuantity = quantity !== undefined ? quantity : 1

  const ecoTaxNet = ecoTax ? ecoTax : 0
  const ecoTaxGross = ecoTax ? ecoTax * FR_TAX_FACTOR : 0

  const listPrice = isB2BCustomer
    ? priceData?.prices?.MinListPrice?.[0]?.net?.value + ecoTaxNet
    : priceData?.prices?.MinListPrice?.[0]?.gross?.value + ecoTaxGross

  const salePrice = isB2BCustomer
    ? priceData?.prices?.MinSalePrice?.[0]?.net?.value + ecoTaxNet
    : priceData?.prices?.MinSalePrice?.[0]?.gross?.value + ecoTaxGross

  const listPriceFormatted = isB2BCustomer
    ? formatPrice(
        isoLocale,
        priceData?.prices?.MinListPrice?.[0]?.net?.value + ecoTaxNet,
        priceData?.prices?.MinListPrice?.[0]?.net.currency
      )
    : addB2CStarIfNeeded(
        priceData?.prices?.MinListPrice?.[0]?.gross?.value + ecoTaxGross,
        formatPrice(
          isoLocale,
          priceData?.prices?.MinListPrice?.[0]?.gross?.value + ecoTaxGross,
          priceData?.prices?.MinListPrice?.[0]?.gross.currency
        )
      )

  const salePriceFormatted = isB2BCustomer
    ? formatPrice(
        isoLocale,
        priceData?.prices?.MinSalePrice?.[0]?.net?.value + ecoTaxNet,
        priceData?.prices?.MinSalePrice?.[0]?.net.currency
      )
    : addB2CStarIfNeeded(
        priceData?.prices?.MinSalePrice?.[0]?.gross?.value + ecoTaxGross,
        formatPrice(
          isoLocale,
          priceData?.prices?.MinSalePrice?.[0]?.gross?.value + ecoTaxGross,
          priceData?.prices?.MinSalePrice?.[0]?.gross.currency
        )
      )
  const currency =
    priceData?.prices?.MinSalePrice?.[0]?.gross.currency || priceData?.prices?.MinListPrice?.[0]?.net.currency || "EUR"
  const discountPercent = salePrice > 0 && listPrice > 0 ? 100 - (salePrice / listPrice) * 100 : 0

  const discountPercentFormatted = formatDiscountPercent(
    determineDisplayDiscountPercent(discountPercent, discountDisplayOptions),
    isoLocale
  )

  return {
    productId: productId,
    listPrice: listPrice,
    salePrice: salePrice,
    discountPercent: discountPercent,
    listPriceFormatted: listPriceFormatted,
    salePriceFormatted: salePriceFormatted,
    discountPercentFormatted: discountPercentFormatted,
    isSinglePrice: false,
    quantity: effectiveQuantity,
    currency: currency,
  }
}

function determineCardPricesForMasterProduct(
  isoLocale: string,
  productId: string,
  isB2BCustomer: boolean,
  prices: ProductPriceContainerCtnrROV1,
  ecoTax: number | undefined,
  quantity?: number,
  discountDisplayOptions?: DiscountDisplayOptions
): CardPrices {
  const priceData = prices?.data?.[0]
  return extractCardPriceFromMasterPriceData(
    isoLocale,
    productId,
    isB2BCustomer,
    priceData,
    ecoTax,
    quantity,
    discountDisplayOptions
  )
}

function addB2CStarIfNeeded(price: number, formatted: string): string {
  if (price < 50) {
    return `${formatted}*`
  }
  return formatted
}
